"use client";

import Image from "next/image";
import Link from "next/link";
import { useForm } from "react-hook-form";

import { processUrlForDemo } from "@/lib/demo/demoUrlProcessing";
import env from "@/lib/env-public";
import { overrideAccountDomain } from "@/lib/testing/override-account-login";

import { withGuestPage } from "@/dashboard/components/auth/with-guest-page";
import { Button } from "@/dashboard/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/dashboard/components/ui/form";
import { Input } from "@/dashboard/components/ui/input";
import { useAuth } from "@/dashboard/contexts/auth-context";
import type { Account } from "@/dashboard/types/account";

type FormVals = { email: string; password: string };

function SignInPage({ accounts }: { accounts: Account[] }) {
  const { signIn } = useAuth();

  const domain = typeof window !== "undefined" ? window.location.hostname : "";
  let lookup = env.NEXT_PUBLIC_DEVELOPER_ACCOUNT_DOMAIN || domain;

  // TODO: Remove this once we have a proper demo environment
  lookup = processUrlForDemo(lookup);
  lookup = overrideAccountDomain(lookup);

  const tenantId = accounts.find(a => a.domain === lookup)?.googleIdpTenantId;

  const form = useForm<FormVals>({ defaultValues: { email: "", password: "" } });

  async function onSubmit(values: FormVals) {
    if (!tenantId) {
      return;
    }

    try {
      await signIn({ ...values, googleIdpTenantId: tenantId });
    } catch (err) {
      form.setError("password", { message: (err as Error).message });
    }
  }

  return (
    <div className="flex flex-col items-center pt-20 min-h-screen">
      <div className="custom-login-box w-[448px] h-[538px] bg-white rounded-lg shadow-md p-8 flex flex-col space-y-8">
        <h2 className="text-[36px] leading-10 text-text-primary-black">
          Sign in to access
          <br />
          your account
        </h2>

        <Form {...form}>
          <form
            onSubmit={evt => void form.handleSubmit(onSubmit)(evt)}
            className="flex flex-col flex-grow space-y-6"
          >
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>EMAIL</FormLabel>
                    <FormControl>
                      <Input type="email" required {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PASSWORD</FormLabel>
                    <FormControl>
                      <Input type="password" required {...field} />
                    </FormControl>
                    <div className="flex justify-end mt-1">
                      <Link
                        href="/forgot-password"
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex-grow" />

            <Button type="submit" className="w-full">
              Login
            </Button>
          </form>
        </Form>
      </div>

      <Image src="/fullnetic.svg" alt="Netic Logo" width={62} height={17} className="mt-8" />
    </div>
  );
}

export default withGuestPage(SignInPage);
